// Client for the report-explorer edge function: the Control Centre's
// "ask about your numbers" bar. The conversation lives only in the caller's
// React state; nothing is persisted, so leaving the dashboard forgets it.

import { supabase } from './supabase';
import type { ReportCardSource } from '../components/agents/ReportCard';

const URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/report-explorer`;

export interface ExplorerTurn {
  question: string;
  answer: string;
  formulas: string[];
}

export type ExplorerEvent =
  | { kind: 'status'; text: string }
  | { kind: 'report'; card: ReportCardSource }
  | { kind: 'answer'; text: string }
  | { kind: 'done' }
  | { kind: 'error'; message: string };

export async function askReportExplorer(
  args: { businessId: string; question: string; turns: ExplorerTurn[] },
  onEvent: (event: ExplorerEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in to ask about your numbers.');
  const response = await fetch(URL, {
    method: 'POST',
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(args),
  });
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `The report question failed (${response.status})`);
  }
  if (!response.body) throw new Error('The report question did not start.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const dataLine = part.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trim()).join('');
      if (!dataLine) continue;
      try {
        onEvent(JSON.parse(dataLine) as ExplorerEvent);
      } catch {
        // malformed chunk; skip
      }
    }
  }
}
